import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { BrandLogo } from '../lib/branding';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Package, 
  Tag, 
  User, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Building2,
  FileText,
  BookOpen
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import OrgSwitcher from './OrgSwitcher';

/**
 * PartnerLayout V2 - Layout unifié pour l'interface Partenaire
 * 
 * Features:
 * - Header Partner dédié (sans barre de recherche globale)
 * - Sidebar fixe avec navigation Partner
 * - Responsive (sidebar collapsible sur mobile)
 * - Cohérence visuelle sur toutes les pages Partner
 */
const PartnerLayout = ({ children }) => {
  const { user, logout, activeOrg, memberships } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Navigation Partner
  const navItems = [
    { 
      path: '/dashboard/partner', 
      label: 'Tableau de bord', 
      icon: LayoutDashboard,
      exact: true
    },
    { 
      path: '/dashboard/partner/produits', 
      label: 'Mes Produits', 
      icon: Package 
    },
    { 
      path: '/dashboard/partner/offres', 
      label: 'Mes Offres', 
      icon: Tag 
    },
    { 
      path: '/dashboard/partner/commandes', 
      label: 'Commandes reçues', 
      icon: FileText 
    },
    { 
      path: '/dashboard/partner/entreprise', 
      label: 'Mon Entreprise', 
      icon: Building2 
    },
    { 
      path: '/dashboard/profil', 
      label: 'Mon Compte', 
      icon: User 
    },
  ];

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--ludo-canvas)' }}>
      {/* Header Partner */}
      <header className="ludo-glass sticky top-0 z-40">
        <div className="flex items-center justify-between h-16 px-4 lg:px-6">
          {/* Logo + Mobile menu */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
              aria-label="Toggle menu"
            >
              {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            
            <Link to="/dashboard/partner" className="flex items-center gap-2.5 group">
              <BrandLogo size="sm" />
            </Link>
          </div>

          {/* Partner badge / Org Switcher */}
          <div className="flex items-center gap-4">
            {/* OrgSwitcher si multi-org, sinon badge statique */}
            {memberships.filter(m => m.status === 'active').length >= 2 ? (
              <OrgSwitcher />
            ) : (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200">
                <Building2 className="h-4 w-4 text-amber-600" />
                <span className="text-sm font-medium text-amber-800">
                  {activeOrg?.org_nom || 'Espace Partenaire'}
                </span>
              </div>
            )}
            
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold" style={{ color: 'var(--ludo-text)' }}>{user?.pseudo || user?.email}</p>
                <p className="text-xs" style={{ color: 'var(--ludo-text-secondary)' }}>Partenaire</p>
              </div>
              <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center text-white">
                <span className="font-semibold text-sm">
                  {(user?.pseudo || user?.email || 'P').charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 lg:pt-[67px] bg-white" style={{ borderRight: '1px solid var(--ludo-border)' }}>
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200",
                    active 
                      ? "text-white" 
                      : "hover:bg-black/[0.04]"
                  )}
                  style={active ? { background: 'var(--ludo-text)', color: 'white' } : { color: 'var(--ludo-text-secondary)' }}
                  data-testid={`nav-${item.path.split('/').pop()}`}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                  {active && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t">
            <Button 
              variant="ghost" 
              className="w-full justify-start text-gray-600 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5 mr-3" />
              Déconnexion
            </Button>
          </div>
        </aside>

        {/* Sidebar - Mobile (overlay) */}
        {sidebarOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
            <aside className="fixed inset-y-0 left-0 w-64 bg-white z-50 lg:hidden pt-16">
              <nav className="flex-1 px-4 py-6 space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item);
                  
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                        active 
                          ? "bg-primary text-white" 
                          : "text-gray-700 hover:bg-gray-100"
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>

              <div className="p-4 border-t">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start text-gray-600 hover:text-red-600 hover:bg-red-50"
                  onClick={handleLogout}
                >
                  <LogOut className="h-5 w-5 mr-3" />
                  Déconnexion
                </Button>
              </div>
            </aside>
          </>
        )}

        {/* Main content */}
        <main className="flex-1 lg:ml-64 overflow-hidden">
          <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-hidden">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default PartnerLayout;
